<template>
  <div class="resource-panel" :style="{width: resourcePanelOpen ? '250px' : '11px'}">
    <div class="header">
      <div v-if="resourcePanelOpen" class="tool resource-panel-back">
        <div class="resource-panel-view-back-button" @click="toggleResources()" title="Toggle Resources"><i class="fal fa-arrow-left"></i></div>
      </div>
      <div class="custom-title no-select">Resources</div>
    </div>
      
    <div class="resource-list" ref="bookResourceList">
      <div class="resource-list-inner" v-if="!loading || !loaded">
        <div v-for="(attachmentGroup) in attachmentGroupsToDisplay" v-bind:key="attachmentGroup.attachments[0]._id">
          <slot v-if="attachmentGroup.attachments.length === 1 && attachmentGroup.attachments[0].resourceObj && !multiResourceOpen">
            <!-- Single resource -->
            
            <ResourcePanelViewItem
              :style="{'margin-top': attachmentGroup.relativeYPos + 'px'}" 
              :title="attachmentGroup.attachments[0].resourceObj.title"
              :img="($config.get('basePathFiles') + (utils.resourceTypeImg(attachmentGroup.attachments[0].resourceObj.category, attachmentGroup.attachments[0].resourceObj.url)))"
              :open="_ => {
                if (!attachmentGroup.attachments[0].locked) {
                  openResource(attachmentGroup.attachments[0])
                }
              }"
              :itemType="attachmentGroup.type"
              v-bind="{resourcePanelOpen}"
              :class="{'overlay': attachmentGroup.attachments[0].locked}"
            />
          </slot>
          <slot v-else-if="attachmentGroup.attachments.length > 1 && !multiResourceOpen">
            
            <!-- Muli resource -->
            <ResourcePanelViewItem 
              :style="{'margin-top': attachmentGroup.relativeYPos + 'px'}"
              title="Multiple Resources"
              :img="($config.get('basePathFiles') + 'static/img/resource-types/annotation_multi.png')"
              :open="_ => multiResourceOpen = attachmentGroup"
              :itemType="attachmentGroup.type"
              v-bind="{resourcePanelOpen}"
            />
          </slot>
          <slot v-else-if="attachmentGroup.attachments.length > 1  && multiResourceOpen === attachmentGroup">
            <!-- Multi resource open-->
            <div :style="{'margin-top': (attachmentGroup.absoluteYPos <= RESOURCE_HEIGHT ? 0 : (attachmentGroup.absoluteYPos - RESOURCE_HEIGHT)) + 'px'}">
              <a @click="multiResourceOpen = undefined;" class="back-btn">&lt; Back</a>
              <div v-for="(attachment, index) in attachmentGroup.attachments" v-bind:key="attachment._id">
                  <ResourcePanelViewItem
                    :index="index"
                    :title="attachment.resourceObj.title"
                    :img="($config.get('basePathFiles') + (utils.resourceTypeImg(attachment.resourceObj.category, attachment.resourceObj.url)))"
                    :open="_ => 
                    {
                      if (!attachment.locked) {
                        openResource(attachment)
                      }
                    }"
                    :itemType="type(attachment)"
                    v-bind="{resourcePanelOpen}"
                    :class="{'overlay': attachment.locked}"
                  />
              </div>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  var epubInstance;

  const _ = require('lodash');
  const RESOURCE_HEIGHT = 32;
  const NUMBER_OF_PAGES_ACTIVE_WITH_RESOURCES = 3;
  
  import utils from '@/utils/utils.js';
  import bookUtils from '@/utils/bookUtils.js';
  import ResourcePanelViewItem from './ResourcePanelViewItem';
  
  export default {
    name: 'ResourcePanelView',
    components: {
      ResourcePanelViewItem
    },
    props: {
      resourcePanelOpen: {
        type: Boolean
      },
      attachments: {
        type: Array
      },
      resources: {
        type: Array
      },
      mpub: {
        type: Boolean,
        required: true
      },
      currentPage: {
        type: Object,
        required: true
      },
      metadata: {
        type: Object,
        required: true
      },
      scale: {
        type: Number,
        required: true
      },
      toggleResources: {
        type: Function,
        required: true
      },
      loaded: {
        type: Boolean,
        required: true
      }
    },
    watch: {
      resources: {
        immediate: true, 
        handler (resources) {
          this.resourcesById = _.keyBy(resources, "_id"); 
          this.debouncedUpdate();
        }
      },
      attachments: {
        immediate: true, 
        handler () {
          this.debouncedUpdate(); 
        }
      },
      currentPage: {
        immediate: true, 
        handler () {
          this.debouncedUpdate();
        }
      },
      scale: {
        immediate: true, 
        handler () {
          this.debouncedUpdate();
        }
      }
    },
    data: function () {
      var t = this;

      return {
        utils: utils,
        attachmentGroupsToDisplay: [],
        attachmentsFiltered: [],
        resourcesById: {},
        debouncedUpdate: _.debounce(this.updateResourcesAndAttachments, 100, { 'maxWait': 1000 }),
        multiResourceOpen: undefined,
        RESOURCE_HEIGHT: RESOURCE_HEIGHT,
        loading: true
      }
    },
    methods: {
      updateResourcesAndAttachments: function() {
        var t = this;
        var prevMultiResourceOpen = t.multiResourceOpen; // used to check: only close it if the multi resource has changed
        t.multiResourceOpen = undefined;

        
        
        // filter out per page
        if(t.mpub) {
          var currentPageObject = bookUtils.getCurrentPageObject(t.metadata, t.currentPage)
          
          t.attachmentsFiltered = _.filter(t.attachments, function(att) {
              return att.section === currentPageObject.file && att.visible;
          });
        } else {
          // load multiple pages for resources
          var pageFromOneBasedIndex = (t.currentPage.oneBasedIndex) - (NUMBER_OF_PAGES_ACTIVE_WITH_RESOURCES - 1)/2;
          var pageToOneBasedIndex = (t.currentPage.oneBasedIndex) + (NUMBER_OF_PAGES_ACTIVE_WITH_RESOURCES - 1)/2;

          t.attachmentsFiltered = _.filter(t.attachments, function(att) {
            var attachmentPageOneBasedIndex = parseInt(att.section);
            return attachmentPageOneBasedIndex >= pageFromOneBasedIndex && attachmentPageOneBasedIndex <= pageToOneBasedIndex && att.visible;
          });
        }

        _.each(t.attachmentsFiltered, function(attachment) {
          attachment.resourceObj = t.resourcesById[attachment.resource];
        });

        // filter out drawing types
        t.attachmentsFiltered = _.filter(t.attachmentsFiltered, function(att) {
          return att.resourceObj && att.resourceObj.category !== "drawing";
        }); 

        // get all LP items to see if resource is locked
        t.attachmentsFiltered = utils.filterLockedResources(t, t.attachmentsFiltered);

        // get Y positions
        t.calculatePositions();

        // check to reopen multiResource
        if(prevMultiResourceOpen && prevMultiResourceOpen.attachments && prevMultiResourceOpen.attachments.length > 0) {
          
          const prevAtt = _.map(prevMultiResourceOpen.attachments, function(att) {
            return att._id;
          }).sort();

          const groupWithFirstAtt = _.find(t.attachmentGroupsToDisplay, function(group) {
            return _.find(group.attachments, function(att) {
              return att._id === prevAtt[0];
            })
          });

          // if the first attachment is still to be found, use it
          if(groupWithFirstAtt) {
              t.multiResourceOpen = groupWithFirstAtt;
          }
        }
      },
      calculatePositions() {
        var t = this;
        var attachments = t.attachmentsFiltered;

      // get y positions of each attachment
        if(t.mpub) {
          var bodyAndWindowHandles = bookUtils.getiFrame();
    
          _.each(attachments, function(att) {
              const yPos = att.selector && att.selector.length > 0 ? utils.getYFromSelector(t, att.selector, bodyAndWindowHandles) : 0;
              att.yPos = yPos;
          });
        } else {
          _.each(attachments, function(att) {
            // percentage out of 100
            if(att.selector && att.section) {
              var pageTopAndBottom = bookUtils.getPDFPageTopAndBottom(parseInt(att.section))
              if(pageTopAndBottom) { 
                att.yPos = pageTopAndBottom.top + (parseFloat(att.selector)/100) * (pageTopAndBottom.bottom - pageTopAndBottom.top);
              } else {
                utils.log(t, "Page NOT found at " + att.section + " : ", pageTopAndBottom);
                att.yPos = 0;
              }
            }
          });
        }

        // sort them by y
        attachments = _.orderBy(attachments, 'yPos')

        // put them into multiple resources buckets
        var lastY = -1;
        var currentGroup = undefined;
        t.attachmentGroupsToDisplay = [];

        _.each(attachments, function(att) {
          // first group
          if(currentGroup === undefined) {
            currentGroup = {
              attachments: [att],
              relativeYPos: att.yPos,
              absoluteYPos: att.yPos,
              type: t.type(att)
            };

            lastY = att.yPos;
            t.attachmentGroupsToDisplay.push(currentGroup);
          } else {
            // if within range of current resource
            if(att.yPos - lastY <= RESOURCE_HEIGHT) {
              currentGroup.attachments.push(att);
              currentGroup.type = t.typeOfGroup(currentGroup);
            } else {
              // make a new group
              currentGroup = {
                attachments: [att],
                relativeYPos: att.yPos - lastY - RESOURCE_HEIGHT,
                absoluteYPos: att.yPos,
                type: t.type(att)
              };

              lastY = att.yPos;

              t.attachmentGroupsToDisplay.push(currentGroup);
            }
          }
        });

        this.attachmentsFiltered = attachments;
      },
      type(attachment) {
        if(attachment.resourceObj && (attachment.resourceObj.private || attachment.resourceObj.category === "sketch" || attachment.resourceObj.category === "note")) {
          return 'annotation';
        } 
        return 'resource';
      },
      typeOfGroup(attachmentGroup) {
        var t = this;
        
        var types = _.uniq(_.map(attachmentGroup.attachments, function(att) {
          return t.type(att);
        }));

        if(types.length === 1) {
          return types[0];
        }

        return 'mixed';
      },
      scrollPanel(ev) {
        var el = ev.target;
        
        if(this.mpub) {
          el = ev.target.documentElement && ev.target.documentElement.scrollTop > 0 ? ev.target.documentElement : ev.target.body;        
        }
        this.$refs.bookResourceList.scrollTop = el.scrollTop;
      },
      openResource: function(attachment) {
        var resource = this.resourcesById[attachment.resource];
        utils.log(this, "resource panel view:",attachment);
        // attachmentOpen: function(handle, attachmentId, attachmentPrivate, attachmentCategory, from = 0)
        if(resource.category === 'note') {
          this.$emit("viewNote", attachment._id)
        } else if(resource.category === 'sketch') {
          this.$emit("viewFreehand", attachment._id)
        } else {
          utils.openResource(this, resource, [attachment._id], 2);
        }
      },
      setPDFInstance() {
        this.loading = false;
        this.debouncedUpdate();
        // this.bookScroller = bookUtils.getiFrame().documentHandle;
        // this.bookScroller.addEventListener('scroll', this.scrollPanel);
      },
      setEpubInstance() {
        this.loading = false;
        epubInstance = bookUtils.getiFrame().documentHandle;
        epubInstance.addEventListener('scroll', this.scrollPanel);
        this.debouncedUpdate();
      }
    },
    beforeDestroy() {
      if(epubInstance) {
        epubInstance.removeEventListener('scroll', this.scrollPanel);
      }
    }
  }

</script>

<style scoped lang="scss">
@import "src/css/vars.scss";

.resource-panel {
  overflow: visible;
}

.resource-list-inner {
  height: 100000px;
}

.resource-list {
  z-index: 599;
  height: calc(100vh - 118px);
  overflow-y: hidden;
  text-align: left;
}

.header {
  display: flex;
  padding: 16px 5px 15px 5px;
  width: 100%;
  border-bottom: 1px solid $background-color-darker3;
  z-index: 601;
  background: white;
  position: fixed;
  top: 62px;
  display: block;
  
  .custom-title {
    flex: 1;
    width: 199px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 30px;
    padding-top: 3px;
  }
}

.back-btn {
  position: absolute;
  padding: 5px 10px;
  display: block;
}

.resource-panel-back {
  font-size: 16pt;
  margin-top: -3px;
}

</style>